// src/hooks/auth/useRecoverySetPasswordForm.ts
import type React from 'react';
import { type FormEvent, useCallback, useMemo, useState } from 'react';

import { useRecoverySetPassword } from '@/hooks/auth/useRecoverySetPassword';
import { passwordSchema } from '@/schemas/recoveryPassword.schema';
import type { SetPasswordStage } from '@/types/AuthState';

const FIRST_PATH_INDEX = 0;

interface UseRecoverySetPasswordFormState {
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  isLoading: boolean;
  isContinueDisabled: boolean;
  error: string | undefined;
  passwordError: string | undefined;
  confirmPasswordError: string | undefined;
  stage: SetPasswordStage | undefined;
}

interface UseRecoverySetPasswordFormHandlers {
  handlePasswordChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleConfirmPasswordChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleToggleShowPassword: () => void;
  handleToggleShowConfirmPassword: () => void;
  handleSubmit: (event: FormEvent) => void;
  handleBack: () => void;
}

interface UseRecoverySetPasswordFormReturn {
  state: UseRecoverySetPasswordFormState;
  handlers: UseRecoverySetPasswordFormHandlers;
}

export function useRecoverySetPasswordForm(): UseRecoverySetPasswordFormReturn {
  const { state: authState, handlers: authHandlers } = useRecoverySetPassword();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});

  const parsed = useMemo(
    () => passwordSchema.safeParse({ password, confirmPassword }),
    [password, confirmPassword],
  );

  const isContinueDisabled =
    authState.isLoading || !parsed.success || authState.stage === 'postCommit';

  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setPassword(event.target.value);
    },
    [],
  );

  const handleConfirmPasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setConfirmPassword(event.target.value);
    },
    [],
  );

  const handleToggleShowPassword = useCallback((): void => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleToggleShowConfirmPassword = useCallback((): void => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent): void => {
      event.preventDefault();
      setValidationErrors({});

      const result = passwordSchema.safeParse({ password, confirmPassword });
      if (!result.success) {
        const newFieldErrors: Record<string, string | undefined> = {};
        for (const issue of result.error.issues) {
          const fieldName = issue.path[FIRST_PATH_INDEX];
          if (typeof fieldName === 'string' && !(fieldName in newFieldErrors)) {
            newFieldErrors[fieldName] = issue.message;
          }
        }
        setValidationErrors(newFieldErrors);
        return;
      }

      void authHandlers.submit(password);
    },
    [password, confirmPassword, authHandlers],
  );

  const handleBack = useCallback((): void => {
    void authHandlers.back();
  }, [authHandlers]);

  const passwordError = validationErrors.password ?? authState.fieldErrors.password;
  const confirmPasswordError =
    validationErrors.confirmPassword ?? authState.fieldErrors.confirmPassword;

  return {
    state: {
      password,
      confirmPassword,
      showPassword,
      showConfirmPassword,
      isLoading: authState.isLoading,
      isContinueDisabled,
      error: authState.error,
      passwordError,
      confirmPasswordError,
      stage: authState.stage,
    },
    handlers: {
      handlePasswordChange,
      handleConfirmPasswordChange,
      handleToggleShowPassword,
      handleToggleShowConfirmPassword,
      handleSubmit,
      handleBack,
    },
  };
}
